import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, listAuditNotes, getReportSchedule, saveReportSchedule, sendReportNow } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTenantData } from '../context/TenantDataContext';
import { useLicense } from '../hooks/useLicense';
import { useFindingGroups, canonicalFindingKey } from '../hooks/useFindingGroups';
import { buildClauses, clauseStats, evidenceFacts } from '../data/complianceModel';
import { effectiveStatus, itemsByIdFrom } from '../utils/policyDisplay';
import Drawer from '../components/Drawer';
import Logo from '../components/Logo';
import PortalIcon from '../components/PortalIcon';
import ChoiceCards from '../components/ChoiceCards';
import { DECISION_LABEL } from '../components/DecisionTypeRadios';
import { normalizePageKey } from '../utils/legacyPageKeys';
import { findingTitle, isCompliancePlaceNote, fmtDateTime } from '../utils/findingLabels';
import { noteArea, areaCategoryLabel } from '../utils/noteContext';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { REPORT_FRAMEWORKS, FRAMEWORK_BY_KEY, frameworkKeyFromSaved } from '../data/reportFrameworks';
import './styles.css';
import './CompliancePage.css';
import './AuditReport.css';

const FREQUENCY_OPTIONS = [
  ['weekly', 'Ugentligt'],
  ['monthly', 'Månedligt'],
  ['quarterly', 'Kvartalsvist'],
];
// The scheduled job runs every night and sends once this many days have
// passed since the last send (see reportScheduleCheck in azure-function).
const FREQUENCY_DAYS = { weekly: 7, monthly: 28, quarterly: 90 };

// Two report kinds: 'framework' (today's report — every finding cited against a chosen framework's own
// paragraphs) and 'setup' (new — a plain status/health snapshot of the M365/Intune estate, no framework
// mapping at all, for a customer who doesn't need CIS 18/ISO/NIS2/GDPR citations, just "how are we
// doing"). Konfiguration's own "Generér rapport" link opens this page with ?type=setup; Compliance's
// keeps opening it with no type (defaults to framework, unchanged).
// The cards on Rapporter: what each report is, in a line, and (for the audience) what it holds.
const KIND_CARDS = [
  { key: 'setup', icon: 'gear', title: 'Opsætning', text: 'Status på jeres Microsoft 365-opsætning, med overblik over enheder og Intune-politikker. Ingen ramme og ingen paragraffer. Til IT-drift og internt overblik.' },
  { key: 'framework', icon: 'shield', title: 'Framework', text: 'Hvert fund vises med den paragraf, det hører under. Til revision, certificering og dokumentation af lovkrav.' },
];
const AUDIENCE_CARDS = [
  { key: 'rev', icon: 'doc', title: 'Revisor', tag: 'Detaljeret', text: 'Bevis og detaljer, så en revisor kan efterprøve alt.', points: ['Hvert krav med status', 'Teknisk evidens for hvert fund (bilag)', 'Alle beslutninger og noter'] },
  { key: 'led', icon: 'eye', title: 'Ledelse', tag: 'Kort', text: 'Kort overblik til dem, der skal beslutte.', points: ['Samlet status i klart sprog', 'Det, der kræver handling', 'De seneste beslutninger'] },
];

function recipientInitials(email) {
  const local = email.split('@')[0] || '?';
  const parts = local.split(/[._-]+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : local.slice(0, 2)).toUpperCase();
}
const fmtLong = (d) => d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });

// The 8 areas keep the keys the saved schedule (and the scheduled e-mail on
// the backend) already know — see REPORT_SECTIONS in azure-function.
const SECTIONS = [
  { key: 'identity', label: 'Brugere' },
  { key: 'roles', label: 'Roller' },
  { key: 'devices', label: 'Enheder' },
  { key: 'security', label: 'Sikkerhed' },
  { key: 'apps', label: 'Entra ID Apps' },
  { key: 'exchange', label: 'E-mail' },
  { key: 'sharepoint', label: 'Grupper' },
  { key: 'licenses', label: 'Licenser' },
];
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));
const DOCS = [
  { key: 'decisions', label: 'Beslutninger' },
  { key: 'notes', label: 'Noter' },
  { key: 'evidence', label: 'Teknisk evidens', revisorOnly: true },
  { key: 'compliant', label: 'Medtag objekter, der opfylder kravet', revisorOnly: true },
];
const ALL_KEYS = [...SECTIONS.map((s) => s.key), ...DOCS.map((d) => d.key)];

const NOTE_PAGE_TO_SECTION = {
  identity: 'identity',
  'guest-users': 'identity',
  roles: 'roles',
  devices: 'devices',
  security: 'security',
  applications: 'apps',
  'intune-apps': 'apps',
  exchange: 'exchange',
  sharepoint: 'sharepoint',
  licenses: 'licenses',
};
const noteSection = (n) => NOTE_PAGE_TO_SECTION[normalizePageKey(n.page)] || null;
const isDecision = (n) => !!(n.decisionType || n.accepted || n.status === 'accepted');

// One row per check NordComply actually makes. `area` is the section the
// row belongs to (so unticking an area removes its rows).
const REQS = [
  { k: 'mfa', area: 'identity', name: 'MFA for alle brugere' },
  { k: 'guests', area: 'identity', name: 'Inaktive konti er ryddet op' },
  { k: 'admin', area: 'roles', name: 'Administratorkonti er begrænset' },
  { k: 'device', area: 'devices', name: 'Enheder lever op til kravene' },
  { k: 'inc', area: 'security', name: 'Trusler overvåges og håndteres' },
  { k: 'apps', area: 'apps', name: 'Tredjepartsapps er gennemgået' },
  { k: 'mail', area: 'exchange', name: 'Ekstern videresendelse er kontrolleret' },
  { k: 'share', area: 'sharepoint', name: 'Ekstern deling er begrænset' },
];
// Same words as Arbejdskø and Beslutninger: I orden / Besluttet / Kræver handling.
const STATUS_LABEL = { g: 'I orden', a: 'Besluttet', r: 'Kræver handling', w: 'Bør gennemgås', i: 'Til orientering', u: 'Ukendt' };
const NOT_YET = 'Ikke tilgængeligt endnu';
const DRAFT_KEY_PREFIX = 'nordcomply-report-draft:';

const plural = (n, one, many) => (n === 1 ? one : many);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function initialSelected() {
  // Everything is in, except the full lists of objects that meet the requirements: they make the report long.
  return Object.fromEntries(ALL_KEYS.map((k) => [k, k !== 'compliant']));
}

// Turns the tenant data the rest of the app already loads into one
// {issue, ev, plain} triple per requirement. `issue: null` means the data
// isn't there — rendered as "Ikke tilgængeligt endnu", never guessed.
function buildChecks(d) {
  const { mfa, risks, devices, security, applications, exchangeInsights, sharePointSettings } = d;
  const checks = {};

  const missing = mfa ? mfa.totalUsers - mfa.mfaEnabledCount : null;
  checks.mfa = mfa
    ? {
        issue: missing > 0,
        ev: missing > 0 ? `${mfa.mfaEnabledCount} af ${mfa.totalUsers} brugere har MFA (${mfa.mfaPercentage}%), ${missing} mangler` : `Alle ${mfa.totalUsers} brugere har MFA`,
        plain: missing > 0 ? `${missing} ${plural(missing, 'bruger mangler', 'brugere mangler')} MFA.` : 'Alle brugere bruger totrinsgodkendelse.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  const inactive = risks?.inactiveAccounts ? risks.inactiveAccounts.length : null;
  checks.guests = inactive !== null
    ? {
        issue: inactive > 0,
        ev: `${inactive} ${plural(inactive, 'inaktiv konto', 'inaktive konti')} (>90 dage), ${risks.guests?.length ?? 0} ${plural(risks.guests?.length ?? 0, 'gæstebruger', 'gæstebrugere')}`,
        plain: inactive > 0 ? `${inactive} ${plural(inactive, 'konto har', 'konti har')} ikke været brugt i over 90 dage.` : 'Ingen konti har stået ubrugt i over 90 dage.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  const globalAdmins = risks?.privilegedRoles ? risks.privilegedRoles.filter((r) => r.role === 'Global Administrator' && r.status !== 'eligible') : null;
  checks.admin = globalAdmins
    ? {
        issue: globalAdmins.length > 4,
        ev: `${globalAdmins.length} permanente Global Administrators`,
        plain: globalAdmins.length > 4 ? `${globalAdmins.length} har permanent administratoradgang. Overvej at gøre nogle PIM-berettigede.` : `Kun ${globalAdmins.length} har permanent administratoradgang.`,
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  const nonCompliant = devices ? Math.max(devices.totalDevices - devices.compliantCount, 0) : null;
  checks.device = devices
    ? {
        issue: nonCompliant > 0,
        ev: `${devices.compliantCount}/${devices.totalDevices} enheder er compliant`,
        plain: nonCompliant > 0 ? `${nonCompliant} ${plural(nonCompliant, 'enhed lever', 'enheder lever')} ikke op til kravene.` : 'Alle enheder lever op til kravene.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  checks.inc = security
    ? {
        issue: (security.openIncidentCount ?? 0) > 0,
        ev: `${security.openIncidentCount ?? 0} åbne Defender-incidents`,
        plain: (security.openIncidentCount ?? 0) > 0 ? `${security.openIncidentCount} åbne trusler mangler at blive lukket.` : 'Ingen åbne trusler.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  const riskyApps = applications?.applications ? applications.applications.filter((a) => a.permissionCount >= 10) : null;
  checks.apps = riskyApps
    ? {
        issue: riskyApps.length > 0,
        ev: `${riskyApps.length} ${plural(riskyApps.length, 'app', 'apps')} med vidtgående adgang`,
        plain: riskyApps.length > 0 ? `${riskyApps.length} ${plural(riskyApps.length, 'app har', 'apps har')} bred adgang og bør gennemgås.` : 'Ingen apps har usædvanlig bred adgang.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  const forwards = exchangeInsights?.forwardingRules ? exchangeInsights.forwardingRules.filter((r) => r.isExternal) : null;
  checks.mail = forwards
    ? {
        issue: forwards.length > 0,
        ev: `${forwards.length} eksterne videresendelser`,
        plain: forwards.length > 0 ? `${forwards.length} ${plural(forwards.length, 'postkasse sender', 'postkasser sender')} mail automatisk ud af huset.` : 'Ingen mails bliver automatisk sendt ud af huset.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  const open = sharePointSettings ? sharePointSettings.sharingRisk === 'bad' || sharePointSettings.sharingRisk === 'warn' : null;
  checks.share = sharePointSettings
    ? {
        issue: open,
        ev: `Ekstern deling: ${sharePointSettings.sharingCapability || NOT_YET}`,
        plain: open ? 'Ekstern deling er åben nok til, at den bør gennemgås.' : 'Ekstern deling følger jeres krav.',
      }
    : { issue: null, ev: NOT_YET, plain: NOT_YET };

  // What already works, as plain numbers with the whole population (x of y), for the "i orden" parts of the reports.
  if (mfa && checks.mfa) checks.mfa.good = `${mfa.mfaEnabledCount} af ${mfa.totalUsers} brugere har MFA`;
  if (risks?.inactiveAccounts && mfa?.totalUsers && checks.guests) checks.guests.good = `${Math.max(mfa.totalUsers - risks.inactiveAccounts.length, 0)} af ${mfa.totalUsers} konti er i brug`;
  if (globalAdmins && checks.admin) checks.admin.good = `${globalAdmins.length} permanente Global Administrators`;
  if (devices && typeof devices.totalDevices === 'number' && checks.device) checks.device.good = `${devices.compliantCount ?? 0} af ${devices.totalDevices} enheder er compliant`;
  if (security && (security.openIncidentCount ?? 0) === 0 && checks.inc) checks.inc.good = 'Ingen åbne trusler';
  if (applications?.applications && checks.apps) checks.apps.good = `${applications.applications.length - (riskyApps || []).length} af ${applications.applications.length} apps har almindelig adgang`;
  return { checks, riskyApps: riskyApps || [], forwards: forwards || [] };
}

// Bilag C: the objects that DO meet the requirements, whole lists. The other side of Bilag A, for the customers who
// ask for everything (an auditor sampling from the full population).
function buildCompliantEvidence(d, extra) {
  const { mfa, risks, devices, applications, exchangeInsights } = d;
  const tables = [];
  const add = (area, title, cols, rows) => { if (rows.length > 0) tables.push({ area, title, cols, rows }); };
  const name = (u) => u.displayName || u.userPrincipalName || '—';
  const inactiveIds = new Set((risks?.inactiveAccounts || []).map((u) => u.id).filter(Boolean));

  add('identity', 'Konti med MFA', ['Konto', 'Status'], (mfa?.users || []).filter((u) => u.hasMfa).map((u) => [name(u), 'Har MFA']));
  add('identity', 'Konti i brug (login inden for 90 dage)', ['Konto', 'Status'], inactiveIds.size > 0 ? (mfa?.users || []).filter((u) => !inactiveIds.has(u.id)).map((u) => [name(u), 'I brug']) : []);
  add('roles', 'Administratorroller via PIM (ikke permanente)', ['Konto og rolle', 'Tildeling'], (risks?.privilegedRoles || []).filter((r) => r.status !== 'permanent').map((r) => [`${r.displayName} — ${r.role}`, 'PIM']));
  add('devices', 'Enheder der er compliant', ['Enhed', 'Status'], (devices?.devices || []).filter((x) => x.complianceState === 'compliant').map((x) => [x.deviceName || x.userDisplayName || '—', 'Compliant']));
  add('devices', 'Politikker i Intune', ['Politik', 'Kategori', 'Platform'], Object.values(extra.policyInv?.categories || {}).flatMap((c) => (c.items || []).map((i) => [i.name, c.label, i.platform || '—'])));
  add('apps', 'Apps med almindelig adgang', ['App', 'Tilladelser'], (applications?.applications || []).filter((a) => !(a.permissionCount >= 10)).map((a) => [a.displayName || a.appId, `${a.permissionCount ?? 0} tilladelser`]));
  add('exchange', 'Mailgodkendelse der er i orden', ['Kontrol', 'Resultat'], [
    ...(exchangeInsights?.emailAuth?.spf?.configured ? [['SPF', 'Konfigureret']] : []),
    ...(exchangeInsights?.emailAuth?.dmarc?.risk === 'ok' ? [['DMARC', 'Håndhævet']] : []),
  ]);
  return tables;
}

// Bilag A — the actual objects behind each check, straight from the same
// data. Every table is one area's list; long lists are capped so a 400-user
// tenant doesn't produce a 40-page PDF (the cap is stated in the table).
function buildEvidence(d, extra) {
  const { mfa, risks, devices, security, sharePointSettings, resources, exchangeInsights, licenses } = d;
  const tables = [];
  const add = (area, reqKey, title, cols, rows) => tables.push({ area, reqKey, title, cols, rows });

  add('identity', 'mfa', 'Konti uden MFA', ['Konto', 'Status'], (mfa?.users || []).filter((u) => !u.hasMfa).map((u) => [u.displayName || u.userPrincipalName, 'Mangler MFA']));
  add('identity', 'guests', 'Inaktive konti (>90 dage)', ['Konto', 'Sidst logget ind'], (risks?.inactiveAccounts || []).map((u) => [u.displayName || u.userPrincipalName, u.daysSinceSignIn === null || u.daysSinceSignIn === undefined ? 'Aldrig' : `${u.daysSinceSignIn} dage siden`]));
  add('identity', 'guests', 'Gæstebrugere', ['Konto', 'Inviteret'], (risks?.guests || []).map((g) => [g.displayName || g.mail, fmtDate(g.invitedDateTime)]));
  add('roles', 'admin', 'Privilegerede roller', ['Konto og rolle', 'Tildeling'], (risks?.privilegedRoles || []).map((r) => [`${r.displayName} — ${r.role}`, r.status === 'permanent' ? 'Permanent' : 'PIM']));
  add('devices', 'device', 'Enheder der ikke er compliant', ['Enhed', 'Status'], (devices?.devices || []).filter((x) => x.complianceState !== 'compliant').map((x) => [x.deviceName || x.userDisplayName || '—', x.complianceState || 'Ukendt']));
  add('security', 'inc', 'Åbne incidents', ['Incident', 'Alvorlighed'], (security?.incidents || []).map((i) => [i.displayName || 'Ukendt', i.severity || 'Ukendt']));
  add('apps', 'apps', 'Apps med vidtgående adgang', ['App', 'Tilladelser'], extra.riskyApps.map((a) => [a.displayName || a.appId, `${a.permissionCount} tilladelser`]));
  add('exchange', 'mail', 'Eksterne videresendelser', ['Postkasse', 'Videresendes til'], extra.forwards.map((r) => [r.mailbox, r.forwardsTo]));
  add('exchange', 'mail', 'Mailgodkendelse', ['Kontrol', 'Resultat'], exchangeInsights?.emailAuth
    ? [
        ['SPF', exchangeInsights.emailAuth.spf?.configured ? 'Konfigureret' : 'Ikke konfigureret'],
        ['DMARC', exchangeInsights.emailAuth.dmarc?.risk === 'ok' ? 'Håndhævet' : exchangeInsights.emailAuth.dmarc?.risk === 'warn' ? 'Kun overvågning' : 'Ikke konfigureret'],
      ]
    : []);
  add('sharepoint', 'share', 'Deling', ['Kontrol', 'Resultat'], [
    ['Ekstern deling', sharePointSettings?.sharingCapability || NOT_YET],
    ['SharePoint-områder', resources?.sharePointSitesCount ?? NOT_YET],
  ]);
  add('licenses', null, 'Licenser', ['Licens', 'Tildelt'], (licenses || []).map((l) => [l.name, `${l.assigned}/${l.total}`]));
  return tables;
}

function EvidenceTable({ cols, rows, notesByRow }) {
  const shown = rows;
  return (
    <div className="rp-tw">
      <table>
        <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={cols.length}>Ingen.</td></tr>}
          {shown.map((r, i) => (
            <tr key={i}>
              {r.map((v, k) => (
                <td key={k}>
                  {v}
                  {k === 0 && (notesByRow?.[i] || []).map((n) => <NoteCard key={n.id} n={n} />)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Status of a requirement in the framework (the same three words as on Compliance).
const CLAUSE_PILL = {
  covered: ['g', 'Understøttet'],
  partial: ['a', 'Delvist understøttet'],
  notFound: ['r', 'Ikke understøttet'],
  unknown: ['u', 'Kan ikke vurderes'],
  external: ['u', 'Ikke i Microsoft 365'],
  unlicensed: ['u', 'Kræver licens'],
};
const CLAUSE_RANK = { notFound: 0, partial: 1, unknown: 2, covered: 3, external: 4, unlicensed: 5 };
function ClausePill({ status }) {
  const [st, label] = CLAUSE_PILL[status] || CLAUSE_PILL.unknown;
  return <span className={`rp-st rp-st--${st}`}>{label}</span>;
}

// Every requirement of the chosen framework with its status, the same assessment as the Compliance page, and which
// policies and rules in force back it.
// CIS groups its safeguards in implementation groups: IG1 is the basic set, IG2 and IG3 add to it.
const IG_TEXT = { 1: 'IG1: grundlæggende cyberhygiejne', 2: 'IG2: virksomheder med flere afdelinger og egen IT-drift', 3: 'IG3: virksomheder med sikkerhedsspecialister og følsomme data' };
function ClauseTable({ clauses, ctx, notes = [] }) {
  const byIg = clauses.some((c) => c.fw === 'cis18' && c.ig);
  const rows = [...clauses].map((c, i) => [c, i])
    .sort(([a, i], [b, j]) => (byIg ? (a.ig || 9) - (b.ig || 9) : 0) || (CLAUSE_RANK[a.status] - CLAUSE_RANK[b.status]) || i - j)
    .map(([c]) => c);
  // The latest decision written on a requirement: shown on its row, the reasoning is under "Bevidste beslutninger".
  const decisionOf = (c) => notes
    .filter((n) => n.decisionType && n.itemKey === `${c.fw}:${c.code}` && n.findingKey === `cmp:${c.fw}`)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null;
  if (rows.length === 0) return <p className="rp-sec">Der er ikke hentet krav for den valgte ramme endnu.</p>;
  return (
    <div className="rp-tw">
      <table>
        <thead><tr><th style={{ width: '40%' }}>Krav</th><th>Status</th><th>Kontroller</th><th>Understøttet af</th></tr></thead>
        <tbody>
          {rows.map((c, ri) => {
            const counted = c.controls.filter((x) => x.eff.status !== 'external' && x.eff.status !== 'unlicensed');
            const met = counted.filter((x) => x.eff.status === 'covered').length;
            const names = [...new Set(c.controls.flatMap((x) => x.evidence.map((e) => evidenceFacts(e, ctx)).filter((f) => f.state === 'Aktiv').map((f) => f.name)))];
            const decision = decisionOf(c);
            const newGroup = byIg && (ri === 0 || (rows[ri - 1].ig || 0) !== (c.ig || 0));
            return (
              <React.Fragment key={`${c.fw}:${c.code}`}>
              {newGroup && <tr className="rp-grp"><td colSpan={4}>{c.ig ? IG_TEXT[c.ig] : 'Uden implementeringsgruppe'}</td></tr>}
              <tr>
                <td><span className="rp-cl">{c.label}{c.ig ? ` · IG${c.ig}` : ''}</span><b>{c.title}</b>{decision && <span className="rp-dec"><DecisionTypePill type={decision.decisionType} /><span className="rp-t">Begrundelse: se Bevidste beslutninger</span></span>}</td>
                <td><ClausePill status={c.status} /></td>
                <td>{counted.length ? `${met} af ${counted.length}` : '—'}</td>
                <td>{names.length ? <div className="rp-pols">{names.map((n) => <span key={n}>{n}</span>)}</div> : <span className="rp-t">Ingen fundet</span>}</td>
              </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Management's view of the decisions: how many, how they are spread, and the ones that need attention now.
function DecisionOverview({ decisions }) {
  const now = Date.now();
  const left = (n) => (n.reviewDate ? Math.ceil((new Date(n.reviewDate).getTime() - now) / 86400000) : null);
  const overdue = decisions.filter((n) => left(n) !== null && left(n) < 0);
  const soon = decisions.filter((n) => left(n) !== null && left(n) >= 0 && left(n) <= 30);
  const noDate = decisions.filter((n) => !n.reviewDate);
  const byType = {};
  decisions.forEach((n) => { const k = n.decisionType || 'accepted'; byType[k] = (byType[k] || 0) + 1; });
  const TYPE_PLURAL = { accepted: ['accepteret risiko', 'accepterede risici'], temp: ['skal rettes', 'skal rettes'], compensating: ['kompenserende kontrol', 'kompenserende kontroller'], falsepos: ['falsk positiv', 'falske positive'], resolved: ['løst', 'løste'] };
  const typeLine = Object.entries(byType).map(([k, n]) => `${n} ${(TYPE_PLURAL[k] || ['beslutning', 'beslutninger'])[n === 1 ? 0 : 1]}`).join(' · ');
  const card = (n) => (
    <div className="rp-dcard" key={n.id}>
      <div className="rp-dcard__top"><b>{n.title || n.pageLabel}</b><DecisionTypePill type={n.decisionType} /></div>
      <small><em className="rp-k">Gælder</em>{areaCategoryLabel(noteArea(n))}{findingTitle(n.findingKey) ? ' · ' + findingTitle(n.findingKey) : ''}</small>
      <small className="rp-why"><em className="rp-k">Begrundelse</em><span className="rp-q">{`“${n.text.length > 170 ? `${n.text.slice(0, 167)}…` : n.text}”`}</span></small>
      <div className="rp-dcard__meta">
        <span><b>Ejer</b> {n.owner || 'Ikke angivet'}</span>
        <span><b>{n.decisionType === 'temp' ? 'Frist' : 'Revurderes'}</b> {n.reviewDate ? `${fmtDate(n.reviewDate)}${left(n) < 0 ? ' · Overskredet' : ''}` : 'Ingen dato'}</span>
      </div>
    </div>
  );
  const list = (title, items) => (items.length > 0 ? (
    <div key={title}>
      <div className="rp-ngrp">{title} ({items.length})</div>
      {items.map(card)}
    </div>
  ) : null);
  return (
    <>
      <h4>Bevidste beslutninger</h4>
      <p className="rp-sec">Afvigelser, ledelsen har valgt at leve med, og hvornår de tages op igen.</p>
      <div className="rp-verdict">
        <b>{decisions.length} {decisions.length === 1 ? 'beslutning' : 'beslutninger'}</b>
        <p>{typeLine}. {overdue.length > 0 ? `${overdue.length} er overskredet. ` : 'Ingen er overskredet. '}{soon.length > 0 ? `${soon.length} skal tages op inden for en måned. ` : ''}{noDate.length > 0 ? `${noDate.length} har ingen revurderingsdato.` : 'Alle har en revurderingsdato.'}</p>
      </div>
      {list('Overskredet, skal tages op nu', overdue)}
      {list('Skal tages op inden for en måned', soon)}
      {list('Uden revurderingsdato', noDate)}
    </>
  );
}

const GROUP_SECTION = { identity: 'identity', 'conditional-access': 'identity', roles: 'roles', devices: 'devices', security: 'security', applications: 'apps', exchange: 'exchange', sharepoint: 'sharepoint', licenses: 'licenses' };

// Every finding with how far its objects are: the full list behind the numbers on the front page.
function FindingsTable({ rows }) {
  if (rows.length === 0) return <p className="rp-sec">Ingen fund i de valgte områder.</p>;
  return (
    <div className="rp-tw">
      <table>
        <thead><tr><th style={{ width: '44%' }}>Fund</th><th>Område</th><th>Objekter</th><th>Besluttet</th><th>Mangler</th></tr></thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.key}>
              <td><b>{f.title}</b>{f.hint && <><br /><span className="rp-t">{f.hint}</span></>}</td>
              <td>{f.section ? SECTION_LABEL[f.section] : '—'}</td>
              <td>{f.total}</td>
              <td>{f.decided}</td>
              <td><StatusPill st={f.open === 0 ? 'a' : f.tone === 'bad' ? 'r' : f.tone === 'warn' ? 'w' : 'i'} />{f.open > 0 && <span className="rp-mono"> {f.open}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ st }) {
  return <span className={`rp-st rp-st--${st}`}>{STATUS_LABEL[st]}</span>;
}

// Same four colors as the live app's Beslutningstype picker (DecisionTypeRadios.jsx) — accepted
// reuses "handled" blue, temp/compensating/falsepos are its own tokens. "resolved" is the retired
// legacy key; falls back to the neutral grey tone rather than a made-up fifth color.
const DECISION_TONE = { accepted: 'accepted', temp: 'temp', compensating: 'compensating', falsepos: 'falsepos', resolved: 'falsepos' };
function DecisionTypePill({ type }) {
  const key = type || 'accepted';
  return <span className={`rp-dt rp-dt--${DECISION_TONE[key] || 'falsepos'}`}>{DECISION_LABEL[key] || 'Accepteret risiko'}</span>;
}

// The tiles count findings (objects), from the same groups as Gennemgang: all findings, those with a decision, and the open
// ones by how serious Gennemgang rates them. Only the red ones "kræver handling". Like Oversigt and Gennemgang, a finding is
// only counted when it is red or orange: information (guests, other role assignments) is listed in the table, not counted.
function Summary({ counts }) {
  return (
    <div className="rp-sum">
      <div className="rp-sum__t"><b>{counts.total}</b><span>Fund i alt</span></div>
      <div className="rp-sum__a"><b>{counts.a}</b><span>Besluttet</span></div>
      <div className="rp-sum__r"><b>{counts.r}</b><span>Kræver handling</span></div>
      {counts.w > 0 && <div className="rp-sum__w"><b>{counts.w}</b><span>Bør gennemgås</span></div>}
    </div>
  );
}

// branding = the tenant's own org's report branding (logo/name), fetched separately from
// /api/organization-branding — shown side by side with NordComply's own logo, equal weight; NordComply's
// mark is never removed, per direct instruction.
function PaperHead({ kind, fw, branding }) {
  return (
    <div className="rp-ph">
      <span className="rp-ph__brands">
        <span className="rp-lg"><Logo size={24} /><span>nord<i>comply</i></span></span>
        {branding?.logo && (
          <>
            <span className="rp-ph__divider" aria-hidden="true" />
            <img src={branding.logo} alt={branding.name || 'Logo'} className="rp-ph__orglogo" />
          </>
        )}
      </span>
      <span className="rp-tag">{kind}{fw && <><br />{fw.label}</>}</span>
    </div>
  );
}

// Opsætningsrapport only — enrolled/compliant/non-compliant tiles plus the Intune policy inventory by
// category. Same tile look as Summary's own rp-sum, just its own set of numbers.
// Just the colored tiles — lives on the cover page alongside Summary's own tiles. The category
// table below is a separate component so it can stay in the normal document flow (with its own page
// break) instead of nesting a second break-before inside the cover page's own break-after.
function HealthTiles({ deviceHealth }) {
  if (!deviceHealth) return null;
  return (
    <div className="rp-sum rp-sum--health">
      <div className="rp-sum__g"><b>{deviceHealth.total}</b><span>Indrullede enheder</span></div>
      <div className="rp-sum__g"><b>{deviceHealth.compliant}</b><span>Compliant</span></div>
      <div className="rp-sum__r"><b>{deviceHealth.nonCompliant}</b><span>Ikke compliant</span></div>
    </div>
  );
}

function PolicyCategoryTable({ policyCategoryCounts }) {
  if (policyCategoryCounts.length === 0) return null;
  return (
    <div className="rp-polcats">
      <h4 className="rp-brk">Intune-politikker pr. kategori</h4>
      <table>
        <tbody>
          {policyCategoryCounts.map((c) => (
            <tr key={c.label}><td>{c.label}</td><td className="rp-mono">{c.count}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// `render` is set when the server's own browser opens the report to make the PDF for a mail (see pages/ReportRender.jsx):
// the choices come from the saved schedule instead of the settings panel, only the paper is drawn, and the page says
// when everything has loaded (window.__NC_REPORT_READY) so the PDF is not taken half way.
const RENDER_FETCHES = 10;

function selectedFromRender(list) {
  const on = new Set(list);
  return Object.fromEntries(ALL_KEYS.map((k) => [k, on.has(k)]));
}

export default function AuditReport({ render = null }) {
  const { user } = useAuth();
  const { tenant, mfa, devices, applications, resources, sharePointSettings, security, ca, membership, loading: tenantLoading } = useTenantData();
  const [settled, setSettled] = useState(0);
  // A Trial can read and print the report (watermarked) but not mail it: see license.js.
  const license = useLicense();
  const emailLocked = !license.features.autoReports;
  const [searchParams] = useSearchParams();
  const [reportKind, setReportKind] = useState(() => render?.kind || (searchParams.get('type') === 'framework' ? 'framework' : 'setup'));
  const [aud, setAud] = useState(render?.aud || 'rev');
  const [fwKey, setFwKey] = useState(() => (render?.fw ? frameworkKeyFromSaved(render.fw) : 'iso'));
  const [selected, setSelected] = useState(() => (render ? selectedFromRender(render.selected) : initialSelected()));
  const [risks, setRisks] = useState(null);
  const [exchangeInsights, setExchangeInsights] = useState(null);
  const [licenses, setLicenses] = useState(null);
  const [sites, setSites] = useState(null);
  const [controlsData, setControlsData] = useState(null);
  const [scopeData, setScopeData] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);
  const [policyInv, setPolicyInv] = useState(null);
  const [branding, setBranding] = useState(null);
  const [notes, setNotes] = useState([]);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false, frequency: 'monthly', recipients: [] });
  const [recipientInput, setRecipientInput] = useState('');
  // The domains a report may be sent to (the customer's own, NordComply and SAC-IT): decided by the server, shown here.
  const [allowedDomains, setAllowedDomains] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testSendResult, setTestSendResult] = useState(null); // { ok: true, sentTo } | { ok: false, error }
  const revisor = aud === 'rev';
  const settingsPanel = useResizablePanel(546, 320, 640, 'right', 'nc-audit-settings-width'); // 420 + 30%, per direct instruction
  const [contentOpen, setContentOpen] = useState(false);
  const fw = FRAMEWORK_BY_KEY[fwKey];

  useEffect(() => {
    if (!tenant) return undefined;
    let cancelled = false;
    const done = () => { if (!cancelled) setSettled((n) => n + 1); };
    apiFetch(`/api/identity-risks?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && !d.error && setRisks(d)).catch(() => {}).finally(done);
    apiFetch(`/api/exchange-insights?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && !d.error && setExchangeInsights(d)).catch(() => {}).finally(done);
    apiFetch(`/api/licenses?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && Array.isArray(d.licenses) && setLicenses(d.licenses)).catch(() => {}).finally(done);
    apiFetch(`/api/sharepoint-sites?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && Array.isArray(d.sites) && setSites(d.sites)).catch(() => {}).finally(done);
    apiFetch(`/api/groups?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && Array.isArray(d.workspaces) && setWorkspaces(d.workspaces)).catch(() => {}).finally(done);
    apiFetch(`/api/controls?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && Array.isArray(d.controls) && setControlsData(d.controls)).catch(() => {}).finally(done);
    apiFetch(`/api/policy-scope?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && !d.error && setScopeData(d)).catch(() => {}).finally(done);
    apiFetch(`/api/policy-inventory?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && !d.error && setPolicyInv(d)).catch(() => {}).finally(done);
    apiFetch(`/api/organization-branding?tenantId=${tenant.tenantId}`).then((r) => r.json()).then((d) => !cancelled && d.branding && setBranding(d.branding)).catch(() => {}).finally(done);
    listAuditNotes(tenant.tenantId).then((d) => !cancelled && Array.isArray(d.notes) && setNotes(d.notes)).catch(() => {}).finally(done);
    if (render) return () => { cancelled = true; };
    getReportSchedule(tenant.tenantId).then((d) => {
      if (cancelled) return;
      if (d.schedule) setSchedule(d.schedule);
      if (Array.isArray(d.allowedDomains)) setAllowedDomains(d.allowedDomains);
    }).catch(() => {});

    try {
      const raw = localStorage.getItem(`${DRAFT_KEY_PREFIX}${tenant.tenantId}`);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft.reportType) setAud(draft.reportType === 'extended' ? 'rev' : 'led');
        if (draft.framework) setFwKey(frameworkKeyFromSaved(draft.framework));
        if (draft.selected) setSelected((prev) => ({ ...prev, ...draft.selected }));
      }
    } catch {
      // no draft, or it didn't parse — start from defaults
    }
    setDraftLoaded(true);
    return () => {
      cancelled = true;
    };
  }, [tenant, render]);

  // The choices are remembered per tenant as they're made — there's no
  // separate "gem kladde" step.
  useEffect(() => {
    if (!tenant || !draftLoaded) return;
    try {
      localStorage.setItem(`${DRAFT_KEY_PREFIX}${tenant.tenantId}`, JSON.stringify({ reportType: aud === 'rev' ? 'extended' : 'general', framework: fw.label, selected }));
    } catch {
      // localStorage unavailable — the choices just won't persist this time
    }
  }, [tenant, draftLoaded, aud, fw, selected]);

  // The server's browser waits for this before it takes the PDF. A moment after the last figure arrives, so the page has drawn.
  const renderReady = !!render && !!tenant && !tenantLoading && settled >= RENDER_FETCHES;
  useEffect(() => {
    if (!renderReady) return undefined;
    // It also says which company the page really loaded, so the server can refuse a PDF made for another one.
    const t = setTimeout(() => { window.__NC_REPORT_FOR = tenant.tenantId; window.__NC_REPORT_READY = true; }, 800);
    return () => clearTimeout(t);
  }, [renderReady, tenant]);

  const data = { mfa, risks, devices, security, applications, exchangeInsights, sharePointSettings, resources, licenses };
  const { checks, riskyApps, forwards } = useMemo(
    () => buildChecks(data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mfa, risks, devices, security, applications, exchangeInsights, sharePointSettings]
  );

  // Opsætningsrapport's own summary — device enrollment/compliance plus the Intune policy inventory by
  // category. Nothing here is mapped to a framework; it's a plain "what does the estate look like"
  // snapshot, for a customer who wants that instead of (or as well as) a framework-mapped report.
  const deviceHealth = devices && typeof devices.totalDevices === 'number'
    ? { total: devices.totalDevices, compliant: devices.compliantCount ?? 0, nonCompliant: Math.max(devices.totalDevices - (devices.compliantCount ?? 0), 0) }
    : null;
  const policyCategoryCounts = useMemo(() => {
    const cats = policyInv?.categories;
    if (!cats) return [];
    // items.length, not the API's own `count` field — the demo-tenant seed caches this same shape
    // without a count (it was never run through toPublicInventory()), and items.length is correct
    // either way.
    return Object.values(cats).filter((c) => !c.error && c.items?.length > 0).map((c) => ({ label: c.label, count: c.items.length }));
  }, [policyInv]);

  // The requirements of the chosen framework with their status: the same assessment as the Compliance page.
  const itemsById = useMemo(() => itemsByIdFrom(policyInv), [policyInv]);
  const clauseFw = ['cis18', 'iso', 'nis2', 'gdpr'].includes(fwKey) ? fwKey : null;
  const clauses = useMemo(() => {
    if (!controlsData || !clauseFw) return [];
    const eff = controlsData.map((c) => ({ ...c, evidence: c.evidence || [], eff: effectiveStatus({ ...c, evidence: c.evidence || [] }, itemsById, { data: scopeData }) }));
    return buildClauses(eff, clauseFw);
  }, [controlsData, itemsById, scopeData, clauseFw]);
  const clauseSummary = clauseStats(clauses);
  const caById = useMemo(() => new Map((ca?.policies || []).map((p) => [p.id, p])), [ca]);
  const clauseCtx = { itemsById, scope: { data: scopeData }, groups: policyInv?.groups, caById };

  // Every finding NordComply shows in Gennemgang (the very same groups), and for each how many of its objects already
  // carry a decision. This is what the report's numbers and its list of findings are built from, so the report can
  // never say less than the app does.
  const { nonEmptyGroups } = useFindingGroups({ mfa, risks, ca, devices, membership, insights: exchangeInsights, applications, security, sites, workspaces, licenses });
  const decidedKeys = useMemo(() => {
    const latest = new Map();
    notes.forEach((n) => {
      if (!n.itemKey || !n.findingKey || !n.decisionType) return;
      const key = `${n.itemKey}::${canonicalFindingKey(n.findingKey)}`;
      const prev = latest.get(key);
      if (!prev || new Date(n.createdAt) > new Date(prev.createdAt)) latest.set(key, n);
    });
    return latest;
  }, [notes]);
  const findingRows = useMemo(() => nonEmptyGroups.map((g) => {
    const total = g.items.length;
    const decided = g.items.filter((i) => decidedKeys.has(`${i.id}::${g.key}`)).length;
    const open = total - decided;
    return { key: g.key, section: GROUP_SECTION[g.area] || null, title: g.title, hint: g.hint, tone: g.tone, counted: g.tone === 'bad' || g.tone === 'warn', total, decided, open, needs: g.tone === 'bad' ? open : 0, review: g.tone === 'warn' ? open : 0, info: g.tone !== 'bad' && g.tone !== 'warn' ? open : 0 };
  }).sort((a, b) => (a.tone === b.tone ? b.open - a.open : a.tone === 'bad' ? -1 : 1)), [nonEmptyGroups, decidedKeys]);
  const shownFindings = findingRows.filter((f) => !f.section || selected[f.section]);
  const baseCounts = shownFindings.filter((f) => f.counted).reduce((c, f) => ({ total: c.total + f.total, a: c.a + f.decided, r: c.r + f.needs, w: c.w + f.review }), { total: 0, a: 0, r: 0, w: 0 });
  const areaFindings = (area) => findingRows.filter((f) => f.counted && f.section === area).reduce((c, f) => ({ total: c.total + f.total, decided: c.decided + f.decided, open: c.open + f.open, needs: c.needs + f.needs, review: c.review + f.review }), { total: 0, decided: 0, open: 0, needs: 0, review: 0 });

  // Decisions have their own register; Bilag B / "Seneste noter" hold the free notes only.
  const freeNotes = notes.filter((n) => !isDecision(n));
  const decisionAreas = useMemo(() => new Set(notes.filter(isDecision).map(noteSection).filter(Boolean)), [notes]);
  const rows = REQS.filter((r) => selected[r.area]).map((r) => {
    const c = checks[r.k];
    const f = areaFindings(r.area);
    // With findings behind it: decided only when none is left open. Without any (the check and the findings read the
    // data differently), fall back to "is there a decision in this area".
    const st = c.issue === null ? 'u' : !c.issue ? 'g' : f.total > 0 ? (f.needs > 0 ? 'r' : f.review > 0 ? 'w' : f.decided > 0 ? 'a' : 'g') : decisionAreas.has(r.area) ? 'a' : 'r';
    const ev = c.issue && f.total > 0 ? `${c.ev}. ${f.decided} af ${f.total} fund er besluttet.` : c.ev;
    return { ...r, ...c, ev, st, ref: fw.ref[r.k] };
  });
  const counts = { g: 0, a: 0, r: 0, w: 0, u: 0 };
  rows.forEach((r) => { counts[r.st] += 1; });
  const known = rows.length - counts.u;

  const areaOn = (n) => {
    const s = noteSection(n);
    return !s || selected[s];
  };
  const decisionNotes = notes.filter((n) => isDecision(n) && areaOn(n)).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  // A decision on something that is not one of Gennemgang's findings (a policy under Konfiguration, a requirement under
  // Compliance) is still a decision: it counts as a finding that has been decided, one per object.
  const groupKeys = new Set(nonEmptyGroups.map((g) => g.key));
  const otherDecided = new Set(decisionNotes.filter((n) => !(n.findingKey && groupKeys.has(canonicalFindingKey(n.findingKey)))).map((n) => (n.itemKey ? `${n.itemKey}::${n.findingKey || ''}` : `note:${n.id || n.createdAt}`))).size;
  const findingCounts = { ...baseCounts, total: baseCounts.total + otherDecided, a: baseCounts.a + otherDecided };
  const clauseForArea = (section) => {
    if (reportKind !== 'framework') return '—';
    const req = REQS.find((r) => r.area === section);
    return req ? fw.ref[req.k][0] : '—';
  };

  const today = new Date().toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
  const author = render ? 'NordComply' : (user?.name || user?.email);

  const toggle = (key) => setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  // Only the options currently shown count (Teknisk evidens is hidden in
  // Ledelse), so "Vælg alle" flips to "Fjern alle" once they're all ticked.
  const shownKeys = [...SECTIONS.map((s) => s.key), ...DOCS.filter((d) => !d.revisorOnly || revisor).map((d) => d.key)];
  const allShownOn = shownKeys.every((k) => selected[k]);
  const toggleAll = () => setSelected((prev) => ({ ...prev, ...Object.fromEntries(shownKeys.map((k) => [k, !allShownOn])) }));

  function handleDownloadPdf() {
    // The browser's own print engine paginates the document (see the
    // @media print rules) — "Save as PDF" suggests this filename.
    const safeName = (tenant?.name || 'tenant').replace(/[^a-z0-9æøå]+/gi, '-');
    const previousTitle = document.title;
    const kindLabel = reportKind === 'framework' ? fw.label : 'Opsaetning';
    document.title = `Audit-rapport-${safeName}-${kindLabel.replace(/[^a-z0-9]+/gi, '-')}-${new Date().toISOString().slice(0, 10)}`;
    window.print();
    document.title = previousTitle;
  }

  function addRecipient() {
    const email = recipientInput.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    const domain = email.split('@')[1].toLowerCase();
    if (allowedDomains && !allowedDomains.includes(domain)) {
      setScheduleError(`Kun adresser på ${allowedDomainsText} kan modtage rapporten. ${email} er ikke tilladt.`);
      return;
    }
    setScheduleError('');
    if (schedule.recipients.includes(email)) {
      setRecipientInput('');
      return;
    }
    setSchedule((s) => ({ ...s, recipients: [...s.recipients, email] }));
    setRecipientInput('');
  }
  // The customer's own domains first (the technical onmicrosoft.com ones left out), then NordComply and SAC-IT.
  const allowedDomainsText = (allowedDomains || []).filter((d) => !d.endsWith('.onmicrosoft.com')).sort((a, b) => Number(['nordcomply.dk', 'sac-it.dk'].includes(a)) - Number(['nordcomply.dk', 'sac-it.dk'].includes(b)) || a.localeCompare(b)).join(', ') || 'nordcomply.dk og sac-it.dk';
  const removeRecipient = (email) => setSchedule((s) => ({ ...s, recipients: s.recipients.filter((r) => r !== email) }));

  async function saveSchedule(enabled) {
    if (!tenant) return;
    setScheduleSaving(true);
    setScheduleError('');
    try {
      const result = await saveReportSchedule(tenant.tenantId, { ...schedule, enabled, reportType: revisor ? 'extended' : 'general', reportKind, framework: reportKind === 'framework' ? fw.label : null, selected });
      if (!result.success) throw new Error(result.error || (enabled ? 'Kunne ikke gemme tidsplanen' : 'Kunne ikke stoppe tidsplanen'));
      setSchedule(result.schedule);
      if (enabled) {
        setScheduleSaved(true);
        setTimeout(() => setScheduleSaved(false), 2000);
      }
    } catch (err) {
      setScheduleError(err.message);
    } finally {
      setScheduleSaving(false);
    }
  }

  // "Send nu" always sends whatever the SAVED schedule holds (the backend never takes ad hoc
  // recipients) — so this saves the form's current state first, preserving whatever enabled/
  // disabled state it already had, then fires the real send. That way "test what I have right now"
  // just works instead of silently testing a stale saved version.
  async function handleTestSend() {
    if (!tenant) return;
    setTestSending(true);
    setTestSendResult(null);
    setScheduleError('');
    try {
      const saveResult = await saveReportSchedule(tenant.tenantId, { ...schedule, reportType: revisor ? 'extended' : 'general', reportKind, framework: reportKind === 'framework' ? fw.label : null, selected });
      if (!saveResult.success) throw new Error(saveResult.error || 'Kunne ikke gemme tidsplanen før test-afsendelse');
      setSchedule(saveResult.schedule);
      const sendResult = await sendReportNow(tenant.tenantId);
      if (!sendResult.ok) throw new Error(sendResult.error || 'Kunne ikke sende test-rapporten');
      setTestSendResult({ ok: true, sentTo: sendResult.sentTo });
    } catch (err) {
      setTestSendResult({ ok: false, error: err.message });
    } finally {
      setTestSending(false);
    }
  }

  // ---------- the report itself (one component tree: preview = print) ----------
  function renderRevisor() {
    const evidence = buildEvidence(data, { riskyApps, forwards }).filter((t) => selected[t.area]);
    const compliantTables = selected.compliant ? buildCompliantEvidence(data, { policyInv }).filter((t) => selected[t.area]) : [];
    // A free note about an object ("Bruger: Anna") is shown right under that
    // object's row in Bilag A (first place it appears); Bilag B keeps the rest.
    const placed = new Set();
    const notesByTable = evidence.map((t) => t.rows.map((r) => {
      const name = String(r[0]);
      const hit = selected.evidence ? freeNotes.filter((n) => !placed.has(n.id) && (n.pageLabel || '').endsWith(`: ${name}`)) : [];
      hit.forEach((n) => placed.add(n.id));
      return hit;
    }));
    const restNotes = freeNotes.filter((n) => !placed.has(n.id));
    const noteAreas = SECTIONS.filter((s) => selected[s.key] && restNotes.some((n) => noteSection(n) === s.key));
    const otherNotes = restNotes.filter((n) => !noteSection(n));
    return (
      <>
        <div className="rp-cover">
          <PaperHead kind="Revisionsrapport" fw={reportKind === 'framework' ? fw : null} branding={branding} />
          <h2 className="rp-title">{tenant?.name}</h2>
          <p className="rp-meta">
            <b>Dato:</b> {today} &nbsp;·&nbsp; <b>Udarbejdet af:</b> {author}{reportKind === 'framework' && <>&nbsp;·&nbsp;<b>Ramme:</b> {fw.tag}</>}
            <br /><b>Datagrundlag:</b> Microsoft 365 (via Microsoft Graph), hentet {today}
          </p>
          <Summary counts={findingCounts} />
          {reportKind === 'setup' && <HealthTiles deviceHealth={deviceHealth} />}
          <p className="rp-cover__note">Data er hentet automatisk fra Microsoft Graph og viser opsætningen på hentetidspunktet. Der kan forekomme mindre afvigelser fra det aktuelle billede, så tallene bør ses som vejledende.</p>
        </div>

        {reportKind === 'setup' && <PolicyCategoryTable policyCategoryCounts={policyCategoryCounts} />}

        <h4 className="rp-brk">Krav og status</h4>
        <p className="rp-sec">Hvert krav vises med status og det bevis, revisoren kan følge op på.</p>
        <div className="rp-tw">
          <table>
            <thead><tr><th style={{ width: '42%' }}>Krav</th><th>Status</th><th>Bevis</th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="3">Ingen områder valgt.</td></tr>}
              {rows.map((r) => (
                <tr key={r.k}>
                  <td>{reportKind === 'framework' && <span className="rp-cl">{r.ref[0]} · {r.ref[1]}</span>}<b>{r.name}</b></td>
                  <td><StatusPill st={r.st} /></td>
                  <td>{r.ev}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {reportKind === 'framework' && clauseFw && (
          <>
            <h4 className="rp-brk">Alle krav i {fw.label}</h4>
            <p className="rp-sec">Samme vurdering som siden Compliance: {clauseSummary.covered} af {clauseSummary.total} krav er understøttet, {clauseSummary.partial} delvist og {clauseSummary.notFound} ikke understøttet{clauseSummary.coverage !== null ? `. Dækning ${clauseSummary.coverage} %` : ''}.</p>
            <ClauseTable clauses={clauses} ctx={clauseCtx} notes={notes} />
          </>
        )}

        <h4 className="rp-brk">Alle fund i NordComply</h4>
        <p className="rp-sec">Det samme som i Gennemgang. Et objekt tæller som besluttet, når kunden har dokumenteret en beslutning på det.</p>
        <FindingsTable rows={shownFindings} />

        <div className="rp-src">
          <p><b>Sådan er rapporten lavet.</b> Data er hentet fra Microsoft 365 via Microsoft Graph. NordComply har ikke ændret noget i miljøet. Beslutninger og noter er skrevet af kundens egne brugere.</p>
          {reportKind === 'framework' && fw.notes.map((t) => <p key={t}>{t}</p>)}
        </div>

        {selected.evidence && (
          <>
            <div className="rp-part"><b>Bilag A · Teknisk evidens</b><span>Objekterne bag hvert krav, hentet direkte fra Microsoft 365.</span></div>
            {evidence.map((t, ti) => (
              <div className="rp-ev" key={`${t.area}-${t.title}`}>
                <h5>{t.title}<span>{reportKind === 'framework' && t.reqKey ? fw.ref[t.reqKey][0] : SECTION_LABEL[t.area]}</span></h5>
                <EvidenceTable cols={t.cols} rows={t.rows} notesByRow={notesByTable[ti]} />
              </div>
            ))}
          </>
        )}

        {selected.notes && (
          <>
            <div className="rp-part"><b>Bilag B · Noter</b><span>Noter, der ikke står under et objekt i Bilag A, skrevet af kundens egne brugere. Vist under det område, de hører til.</span></div>
            {restNotes.length === 0 && <p className="rp-sec">Ingen noter ud over dem, der står under objekterne i Bilag A.</p>}
            {noteAreas.map((s) => (
              <div key={s.key}>
                <div className="rp-ngrp">{s.label}</div>
                {restNotes.filter((n) => noteSection(n) === s.key).map((n) => <NoteCard key={n.id} n={n} />)}
              </div>
            ))}
            {otherNotes.length > 0 && (
              <div>
                <div className="rp-ngrp">Øvrige noter</div>
                {otherNotes.map((n) => <NoteCard key={n.id} n={n} />)}
              </div>
            )}
          </>
        )}

        {selected.compliant && (
          <>
            <div className="rp-part"><b>Bilag C · Objekter, der opfylder kravet</b><span>De objekter, der lever op til kravene. Hele listerne, uden begrænsning, så man kan stikprøve i det fulde grundlag.</span></div>
            {compliantTables.length === 0 && <p className="rp-sec">Ingen objekter at vise for de valgte områder.</p>}
            {compliantTables.map((t) => (
              <div className="rp-ev" key={`c-${t.area}-${t.title}`}>
                <h5>{t.title}<span>{SECTION_LABEL[t.area]} · {t.rows.length}</span></h5>
                <EvidenceTable cols={t.cols} rows={t.rows} />
              </div>
            ))}
          </>
        )}

        {selected.decisions && (
          <>
            <div className="rp-part"><b>Beslutninger</b><span>Alle afvigelser, der er besluttet bevidst, med ejer og revurderingsdato.</span></div>
            {/* A 5-column table (Beslutning/Type/Ejer/Revurderes/Krav) doesn't fit portrait's width —
                one card per decision instead: type as a badge top-right, Ejer/Revurderes/Krav as a
                compact meta line along the bottom rather than three separate narrow columns. */}
            {decisionNotes.length === 0 && <p className="rp-sec">Ingen dokumenterede beslutninger.</p>}
            {decisionNotes.map((n) => (
              <div className="rp-dcard" key={n.id}>
                <div className="rp-dcard__top">
                  <b>{n.title || n.pageLabel}</b>
                  <DecisionTypePill type={n.decisionType} />
                </div>
                <small><em className="rp-k">Gælder</em>{areaCategoryLabel(noteArea(n))}{findingTitle(n.findingKey) ? ' · ' + findingTitle(n.findingKey) : ''}</small>
                <small className="rp-why"><em className="rp-k">Begrundelse</em><span className="rp-q">{`“${n.text}”`}</span></small>
                <div className="rp-dcard__meta">
                  <span><b>Ejer</b> {n.owner || 'Ikke angivet'}</span>
                  <span><b>{n.decisionType === 'temp' ? 'Frist' : 'Revurderes'}</b> {n.reviewDate ? `${fmtDate(n.reviewDate)}${new Date(n.reviewDate) < new Date() ? ' · Overskredet' : ''}` : 'Ikke fastsat'}</span>
                  <span><b>Krav</b> {noteSection(n) ? clauseForArea(noteSection(n)) : '—'}</span>
                  <span><b>Oprettet</b> {fmtDateTime(n.createdAt)}</span>
                </div>
              </div>
            ))}
          </>
        )}

        <Signature author={author} today={today} />
      </>
    );
  }

  function renderLedelse() {
    const open = rows.filter((r) => r.st === 'r');
    const heading = findingCounts.total > 0
      ? (findingCounts.r > 0 ? (findingCounts.r === 1 ? 'Ét fund kræver handling.' : `${findingCounts.r} fund kræver handling.`)
        : findingCounts.w > 0 ? `Intet kræver handling. ${findingCounts.w === 1 ? 'Ét fund bør' : `${findingCounts.w} fund bør`} gennemgås.` : 'Intet kræver handling.')
      : (counts.r === 0 ? (counts.a === 0 ? 'Alt er på plads.' : 'Alt er på plads eller besluttet.') : counts.r === 1 ? 'Én ting skal rettes.' : `${counts.r} ting skal rettes.`);
    const openFindings = shownFindings.filter((f) => f.needs > 0);
    const latestNotes = [...freeNotes].filter(areaOn).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 2);
    return (
      <>
        <div className="rp-cover">
          <PaperHead kind="Ledelsesrapport" fw={reportKind === 'framework' ? fw : null} branding={branding} />
          <h2 className="rp-title">{tenant?.name}</h2>
          <p className="rp-meta"><b>Dato:</b> {today} &nbsp;·&nbsp; <b>Udarbejdet af:</b> {author}</p>
          <div className="rp-verdict">
            <b>{heading}</b>
            <p>{reportKind === 'framework' ? fw.lead : 'Status på jeres Microsoft 365- og Intune-opsætning.'} Her er status på de tekniske krav i Microsoft 365: {counts.g} af {known} er helt på plads, {counts.a} er bevidst besluttet, {counts.r} mangler at blive rettet{counts.w > 0 ? `, og ${counts.w} bør gennemgås` : ''}.</p>
          </div>
          <Summary counts={findingCounts} />
          {reportKind === 'setup' && <HealthTiles deviceHealth={deviceHealth} />}
          <p className="rp-cover__note">Data er hentet automatisk fra Microsoft Graph og viser opsætningen på hentetidspunktet. Der kan forekomme mindre afvigelser fra det aktuelle billede, så tallene bør ses som vejledende.</p>
        </div>

        {reportKind === 'setup' && <PolicyCategoryTable policyCategoryCounts={policyCategoryCounts} />}

        <h4>Det kræver handling</h4>
        <p className="rp-sec">Kun det, ledelsen skal sørge for bliver gjort.</p>
        {open.length === 0 && <p className="rp-sec">Intet kræver handling lige nu.</p>}
        {open.map((r) => (
          <div className="rp-act" key={r.k}>
            <span className="rp-act__ic">!</span>
            <div><b>{r.name}</b><p>{r.plain}</p>{reportKind === 'framework' && <div className="rp-act__who">Krav {r.ref[0]}</div>}</div>
          </div>
        ))}

        {openFindings.length > 0 && (
          <>
            <h4>Fund, der kræver handling</h4>
            <p className="rp-sec">De vigtigste først. Den fulde liste ligger i rapporten til revisor.</p>
            {openFindings.map((f) => (
              <div className="rp-act" key={f.key}>
                <span className="rp-act__ic">!</span>
                <div><b>{f.title}</b><p>{f.needs} af {f.total} {f.total === 1 ? 'objekt' : 'objekter'} mangler en beslutning.</p></div>
              </div>
            ))}
          </>
        )}

        {reportKind === 'framework' && clauseFw && clauses.length > 0 && (
          <>
            <h4>Hvor godt understøtter Microsoft 365 {fw.label}?</h4>
            <p className="rp-sec">{clauseSummary.coverage !== null ? `Dækning ${clauseSummary.coverage} %. ` : ''}{clauseSummary.covered} af {clauseSummary.total} krav er understøttet, {clauseSummary.partial} delvist og {clauseSummary.notFound} ikke.</p>
            {clauses.filter((c) => c.status === 'notFound').map((c) => (
              <div className="rp-act" key={c.code}><span className="rp-act__ic">!</span><div><b>{c.title}</b><p>Ikke understøttet. Krav {c.label}.</p></div></div>
            ))}
          </>
        )}

        {rows.some((r) => r.st === 'g' || r.good) && (
          <>
            <h4>Det, der er i orden</h4>
            <p className="rp-sec">Det, der allerede virker, med tal for hele grundlaget.</p>
            <div className="rp-lrs">
              {rows.filter((r) => r.st === 'g' || r.good).map((r) => (
                <div className="rp-lr" key={r.k}>
                  <span className="rp-dot rp-dot--g" />
                  <span><b>{r.name}</b><br /><span className="rp-t">{r.good || r.plain}</span></span>
                  {reportKind === 'framework' && <span className="rp-mono">{r.ref[0]}</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {selected.decisions && decisionNotes.length > 0 && <DecisionOverview decisions={decisionNotes} />}

        <h4>Status pr. område</h4>
        <p className="rp-sec">Grøn er i orden. Gul er besluttet. Rød kræver handling.</p>
        <div className="rp-lrs">
          {rows.map((r) => (
            <div className="rp-lr" key={r.k}>
              <span className={`rp-dot rp-dot--${r.st}`} />
              <span><b>{r.name}</b><br /><span className="rp-t">{r.plain}</span></span>
              {reportKind === 'framework' && <span className="rp-mono">{r.ref[0]}</span>}
            </div>
          ))}
        </div>

        {selected.notes && latestNotes.length > 0 && (
          <>
            <h4>Seneste noter</h4>
            <p className="rp-sec">Fra teamet. Alle noter ligger i den tekniske rapport til revisor.</p>
            {latestNotes.map((n) => <NoteCard key={n.id} n={n} />)}
          </>
        )}

        <Signature author={author} today={today} />
      </>
    );
  }

  const visibleDocs = DOCS.filter((d) => !d.revisorOnly || revisor);

  if (render) {
    return (
      <div className="cmp rp-page rp-render">
        <div className="rp-pv"><div className="rp-pvb">
          <div className={`rp-paper${license.features.reportWatermark ? ' rp-paper--trial' : ''}`}>{revisor ? renderRevisor() : renderLedelse()}</div>
        </div></div>
      </div>
    );
  }

  return (
    <div className="cmp rp-page">
      <div className="head">
        <div>
          <div className="eye">Dokumentation · Til revisor</div>
          <h1>Rapporter</h1>
          <p>Færdige rapporter til ledelse og revisor. Vælg målgruppe, ramme og indhold. Preview&apos;et viser præcis det, der bliver hentet.</p>
        </div>
      </div>

      <Drawer
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        eyebrow="Rapporter"
        title="Send automatisk"
        subtitle="Rapporten sendes som mail til dem, du vælger."
        className="rp-sched"
        footer={(
          <div className="rp-sched__foot">
            <button
              type="button"
              className="btn-secondary rp-sched__test"
              disabled={emailLocked || testSending || scheduleSaving || schedule.recipients.length === 0}
              onClick={handleTestSend}
            >
              {testSending ? 'Genererer…' : 'Send nu'}
            </button>
            <button type="submit" form="rp-sched-form" className="btn-primary rp-sched__save" disabled={emailLocked || scheduleSaving || schedule.recipients.length === 0}>
              {scheduleSaving ? 'Gemmer…' : scheduleSaved ? '✓ Gemt' : schedule.enabled ? 'Gem ændringer' : 'Aktivér afsendelse'}
            </button>
            {schedule.enabled && (
              <button type="button" className="rp-sched__stop" disabled={scheduleSaving} onClick={() => saveSchedule(false)}>Stop</button>
            )}
          </div>
        )}
      >
        <form id="rp-sched-form" onSubmit={(e) => { e.preventDefault(); if (!emailLocked) saveSchedule(true); }} className="rp-sched__form">
          {emailLocked && <p className="modal-msg modal-msg--error">Automatisk afsendelse og test-mail er ikke med i en prøveperiode. Du kan stadig hente rapporten som PDF (med vandmærke).</p>}
          <div className={`rp-sched__status${schedule.enabled ? ' is-on' : ''}`}>
            <span className="rp-sched__pulse" />
            <span>
              <b>{schedule.enabled ? 'Aktiv' : 'Ikke aktiv'}</b>
              <small>
                {schedule.enabled
                  ? schedule.lastSentAt
                    ? `Sidst sendt ${fmtLong(new Date(schedule.lastSentAt))}. Næste afsendelse ca. ${fmtLong(new Date(new Date(schedule.lastSentAt).getTime() + (FREQUENCY_DAYS[schedule.frequency] || 28) * 86400000))}.`
                    : 'Første afsendelse sker i nat.'
                  : 'Første afsendelse sker, natten efter du gemmer.'}
              </small>
            </span>
          </div>

          <div>
            <p className="rp-sched__lab">Hvor tit</p>
            <div className="rp-sched__freq">
              {FREQUENCY_OPTIONS.map(([key, label]) => (
                <button key={key} type="button" className={`rp-sched__fq${schedule.frequency === key ? ' is-on' : ''}`} onClick={() => setSchedule((sc) => ({ ...sc, frequency: key }))}>
                  <b>{label}</b><small>Hver {FREQUENCY_DAYS[key]}. dag</small>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="rp-sched__lab">Hvortil</p>
            <div className="rp-sched__add">
              <PortalIcon name="mail" size={16} />
              <input
                type="email"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="navn@virksomhed.dk"
                aria-label="Modtagerens mailadresse"
              />
              <button type="button" onClick={addRecipient}>Tilføj</button>
            </div>
            {schedule.recipients.length > 0 ? (
              <div className="rp-sched__rcpt">
                {schedule.recipients.map((r) => (
                  <div className="rp-sched__rc" key={r}>
                    <span className="rp-sched__av">{recipientInitials(r)}</span>
                    <span>{r}</span>
                    <button type="button" aria-label={`Fjern ${r}`} onClick={() => removeRecipient(r)}><PortalIcon name="x" size={14} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="rp-sched__note">Tilføj mindst én modtager.</p>
            )}
          </div>

          {allowedDomains && <p className="rp-sched__note">Kun adresser på {allowedDomainsText} kan modtage rapporten.</p>}

          {testSending && <p className="modal-msg">Genererer rapport. Kan tage et øjeblik.</p>}

          {testSendResult && (
            testSendResult.ok
              ? <p className="modal-msg modal-msg--success">Test-rapport sendt til {testSendResult.sentTo.join(', ')}.</p>
              : <p className="modal-msg modal-msg--error">{testSendResult.error}</p>
          )}
          {scheduleError && <p className="modal-msg modal-msg--error">{scheduleError}</p>}
        </form>
      </Drawer>

      <div className="rp-grid" style={{ gridTemplateColumns: `${settingsPanel.width}px 20px minmax(0, 1fr)` }}>
        <div className="rp-set">
          <div className="rp-set__body">
            <div className="rp-grp">
              <p className="rp-lab">Rapporttype</p>
              <ChoiceCards label="Rapporttype" options={KIND_CARDS} value={reportKind} onChange={setReportKind} />
            </div>

            <div className="rp-grp">
              <p className="rp-lab">Til hvem</p>
              <ChoiceCards label="Til hvem" options={AUDIENCE_CARDS} value={revisor ? 'rev' : 'led'} onChange={setAud} />
            </div>

            <div className="rp-grp">
              <p className="rp-lab">Ramme</p>
              {reportKind === 'setup' ? (
                <p className="rp-rd-off">Opsætningsrapporten er ikke knyttet til en ramme, så der er ingen at vælge.</p>
              ) : (
                <div className="rp-fw" role="radiogroup" aria-label="Ramme">
                  {REPORT_FRAMEWORKS.map((f) => (
                    <button type="button" key={f.key} role="radio" aria-checked={fwKey === f.key} className={`rp-fw__r${fwKey === f.key ? ' is-on' : ''}`} onClick={() => setFwKey(f.key)}>
                      <span className="rp-fw__dot"><PortalIcon name="check" size={13} /></span>
                      <span>{f.label}</span>
                      <small className={`rp-fw__law rp-fw__law--${f.law ? 'y' : 'n'}`}>{f.law ? 'Lovkrav' : 'Frivillig'}</small>
                      {fwKey === f.key && <span className="rp-fw__d">{f.what} {f.who}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="rp-grp">
              <p className="rp-lab">Indhold</p>
              {!contentOpen ? (
                <div className="rp-incl">
                  <div>
                    <b>{allShownOn ? 'Alt er med' : `${shownKeys.filter((k) => selected[k]).length} af ${shownKeys.length} dele er med`}</b>
                    <span>Krav, status{revisor ? ', teknisk evidens' : ''}, beslutninger og noter</span>
                  </div>
                  <button type="button" className="rp-link" onClick={() => setContentOpen(true)} aria-expanded={false}>Tilpas</button>
                </div>
              ) : (
                <>
                  <div className="rp-incl__bar">
                    <button type="button" className="rp-link" onClick={toggleAll}>{allShownOn ? 'Fjern alle' : 'Vælg alle'}</button>
                    <button type="button" className="rp-link" onClick={() => setContentOpen(false)} aria-expanded>Skjul</button>
                  </div>
                  <div className="rp-sub">Krav og status</div>
                  <div className="rp-cols">
                    {SECTIONS.map((s) => (
                      <button key={s.key} type="button" role="checkbox" aria-checked={!!selected[s.key]} className={`rp-cr${selected[s.key] ? ' is-on' : ''}`} onClick={() => toggle(s.key)}>
                        <i><PortalIcon name="check" size={10} /></i>{s.label}
                      </button>
                    ))}
                  </div>
                  <div className="rp-sub">Dokumentation</div>
                  <div className="rp-cols">
                    {visibleDocs.map((d) => (
                      <button key={d.key} type="button" role="checkbox" aria-checked={!!selected[d.key]} className={`rp-cr${selected[d.key] ? ' is-on' : ''}${d.key === 'compliant' ? ' rp-cr--wide' : ''}`} onClick={() => toggle(d.key)}>
                        <i><PortalIcon name="check" size={10} /></i>{d.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="rp-set__foot">
            <div className="rp-acts">
              <button type="button" className="btn-secondary rp-btn" onClick={() => setScheduleOpen(true)}>
                <PortalIcon name="calendar" size={15} />
                {schedule.enabled ? `Sendes ${FREQUENCY_OPTIONS.find(([k]) => k === schedule.frequency)?.[1].toLowerCase()}` : 'Send automatisk'}
              </button>
              <button type="button" className="btn-primary rp-btn" onClick={handleDownloadPdf}>
                <PortalIcon name="download" size={15} />Hent PDF
              </button>
            </div>
          </div>
        </div>

        <div className={`resize-handle rp-handle${settingsPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={settingsPanel.onMouseDown} />

        <div className="rp-pv">
          <div className="rp-pvh"><span>Forhåndsvisning · {revisor ? 'Revisor' : 'Ledelse'} · {reportKind === 'framework' ? fw.label : 'Opsætningsrapport'}</span><em>Rul for at se hele rapporten</em></div>
          <div className="rp-pvb">
            <div className={`rp-paper${license.features.reportWatermark ? ' rp-paper--trial' : ''}`}>{revisor ? renderRevisor() : renderLedelse()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// A note always says what it is ("Note") and what it belongs to — the
// pageLabel it was written on, which for object-level notes is the object
// itself (e.g. "Bruger: Martin Mikkelsen") — so it can't be mistaken for
// report text and can be traced back to its area.
function NoteCard({ n }) {
  return (
    <div className="rp-nt">
      <div className="rp-nt__top">
        <span className="rp-nt__label"><PortalIcon name="edit" size={11} />Note</span>
        <span className="rp-nt__where">{n.pageLabel || SECTION_LABEL[noteSection(n)] || 'Øvrige'}</span>
        {n.decisionType && <DecisionTypePill type={n.decisionType} />}
      </div>
      {findingTitle(n.findingKey) && <div className="rp-nt__fund">{isCompliancePlaceNote(n) ? 'Gælder' : 'Fund'}: {findingTitle(n.findingKey)}</div>}
      <p>{n.text}</p>
      <small>{n.authorName} · {fmtDateTime(n.createdAt)}</small>
    </div>
  );
}

function Signature({ author, today }) {
  return (
    <div className="rp-sig">
      <span>Udarbejdet af<b>{author}</b></span>
      <span>Dato<b>{today}</b></span>
      <span>Version<b>1</b></span>
    </div>
  );
}
